/* Catalog.js */
class Catalog {
    constructor(waiting, numbro, bootstrap, toastNotifier, translator) {
        if (!translator) throw new Error('Catalog: translator is required');
        this.waiting = waiting;
        this.numbro  = numbro;
        this.bootstrap  = bootstrap;
        this.toastNotifier = toastNotifier;
        this.translator = translator;
        this.baseUrl   = null;
        this.container = null;
        this.timeout   = null;
        this.init();
    }


    getToastTitle(type) {
        if (type === 'success') return this.translator.trans('common.success');
        if (type === 'danger' || type === 'error') return this.translator.trans('common.error');
        return this.translator.trans('common.info');
    }

    getPortfolioFallbackError() {
        return this.translator.trans('catalog.portfolioUpdateError');
    }

    eachCartRow(callback) {
        const nodeList = document.querySelectorAll('[data-catalog-namespace="catalog/cart/product"]');
        return Array.from(nodeList).map(currentNode => {
            const code = currentNode.getAttribute('data-catalog-code');
            const inputPrice = parseFloat(currentNode.getAttribute('data-catalog-price')) || 0;
            const inputQty = parseInt(
                currentNode.querySelector('[data-catalog-namespace="catalog/cart/product/qty"]').value
            ) || 0;
    
            const object = { code, qty: inputQty, price: inputPrice };
    
            // Pass both the object and the node to the callback
            if (callback) callback(object, currentNode);
    
            return object;
        });
    }
    
    // Save cart data with a delay
    ajaxSaveCart(data, timing, callback) {
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
            this.waiting.wait(true);
            const items = Object.fromEntries(data.map(val => [val.code, val]));
            const post = {
                items,
                result: callback ? 'html' : ''
            };

            fetch(this.baseUrl + '/save-cart', {
                method: 'POST',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify(post)
            })
            .then(async response => {
                const result = await response.json();
                if (!response.ok || !result.success) {
                    throw new Error(result.message || `HTTP ${response.status}`);
                }
                return result;
            })
            .then(result => {
                if (result.flashes && this.toastNotifier) {
                    Object.entries(result.flashes).forEach(([type, messages]) => {
                        messages.forEach(message => {
                            this.toastNotifier.show({
                                type,
                                title: this.getToastTitle(type),
                                message
                            });
                        });
                    });
                } else if (result.message && this.toastNotifier) {
                    this.toastNotifier.success(result.message, { title: this.getToastTitle('success') });
                }

                this.container.dispatchEvent(new CustomEvent('catalog-cart-update', {
                    bubbles: true,
                    detail: { quantity: result.quantity, total: null }
                }));

                if (callback) {
                    callback(result);
                }
            })
            .catch(error => {
                console.error(error);
                if (this.toastNotifier) {
                    this.toastNotifier.error(error.message || this.getPortfolioFallbackError(), {
                        title: this.getToastTitle('error')
                    });
                }
            })
            .finally(() => this.waiting.wait(false));
        }, timing);
    }

    // Switch view between BOX and ROW
    ajaxSwitchView(type, callback) {
        if (["BOX", "ROW"].includes(type)) {
            this.waiting.wait(true);
            fetch(this.baseUrl + '/switch-view', {
                method: 'POST',
                cache: 'no-cache',
                credentials: 'same-origin',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({ type })
            })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok: ' + response.statusText);
                return response.text();
            })
            .then(data => callback && callback(data))
            .catch(console.error)
            .finally(() => this.waiting.wait(false));
        }
    }

    // Update the query string parameter
    updateQueryStringParameter(uri, key, value) {
        const separator = uri.includes('?') ? "&" : "?";
        const re = new RegExp(`([?&])${key}=.*?(&|$)`, "i");
        return uri.match(re) ? uri.replace(re, `$1${key}=${value}$2`) : uri + separator + key + '=' + value;
    }

    // Event handler for click events
    clickEvent(event) {
        const trigger = event.target.closest(
            '[data-catalog-action="switch-view-box"], ' +
            '[data-catalog-action="switch-view-row"], ' +
            '[data-catalog-action="cart-item-add"], ' +
            '[data-catalog-action="cart-item-delete"]'
        );
        if (!trigger) return true; // No catalog action target
        // event.preventDefault();

        switch (true) {
            case trigger.matches('[data-catalog-action="switch-view-box"]'):
            case trigger.matches('[data-catalog-action="switch-view-row"]'):
                const mode = trigger.matches('[data-catalog-action="switch-view-box"]') ? 'BOX' : 'ROW';
                this.ajaxSwitchView(mode, data => {
                    document.querySelector('[data-catalog-namespace="catalog/switchview"]').innerHTML = data;
                    location.search = this.updateQueryStringParameter(location.search, 'page', '1');
                });
                break;
            case trigger.matches('[data-catalog-action="cart-item-add"]'):
            case trigger.matches('[data-catalog-action="cart-item-delete"]'):
                const row = trigger.closest('[data-catalog-namespace="catalog/cart/product"], [data-catalog-namespace="catalog/product"]');
                const qty = trigger.matches('[data-catalog-action="cart-item-add"]') ? '+1' : '0';
                const code = row.getAttribute('data-catalog-code');
                const cartContent = document.querySelector('[data-catalog-namespace="catalog/cart/content"]');
                const callback = cartContent
                    ? result => {
                        if (result.html !== null) {
                            cartContent.innerHTML = result.html;
                            this.updateCart();
                        }
                    }
                    : null;
                this.ajaxSaveCart([{ code, qty }], 10, callback);
                break;
            default:
                return true; // Allow default behavior
        }
    }

    // Update cart totals immediately when a quantity field changes.
    inputEvent(event) {
        if (!event.target.matches('[data-catalog-namespace="catalog/cart/product/qty"]')) {
            return;
        }

        const data = this.updateCart();
        this.ajaxSaveCart(data, 1500);
    }

    updateCart() {
        let quantity = 0;
        let total = 0;
    
        const data = this.eachCartRow((object, currentNode) => {
            // Now we select price and total from the *current* row
            const rowPrice = currentNode.querySelector('[data-catalog-namespace="catalog/cart/product/price"]');
            const rowTotalNode = currentNode.querySelector('[data-catalog-namespace="catalog/cart/product/total"]');
    
            if (rowPrice) {
                rowPrice.textContent = object.price
                    ? this.numbro(object.price).format({ thousandSeparated: true, mantissa: 2 })
                    : '';
            }
    
            quantity += object.qty;
    
            if (rowTotalNode) {
                const rowTotal = object.qty * object.price;
                rowTotalNode.textContent = this.numbro(rowTotal).format({ thousandSeparated: true, mantissa: 2 });
                total += rowTotal;
            }
        });
    
        // Dispatch a custom event to update total quantity & amount displayed elsewhere
        this.container.dispatchEvent(new CustomEvent('catalog-cart-update', {
            bubbles: true,
            detail: { quantity, total }
        }));
    
        return data;
    }

    // Return from the cart page.
    closeCart() {
        window.history.back();
    }

    // Open the cart as a dedicated page.
    showCart() {
        window.location.href = `${this.baseUrl}/cart`;
    }

    // Initialize the catalog
    init() {
        console.log('Catalog initialization');
        this.container = document.querySelector('[data-catalog-namespace="catalog"]');
        this.baseUrl = this.container.getAttribute('data-catalog-baseurl');
        this.container.addEventListener('click', this.clickEvent.bind(this));
        this.container.addEventListener('input', this.inputEvent.bind(this));
        this.container.addEventListener('catalog-cart-update', event => {
            const { quantity, total } = event.detail;
            const catalogCartNum = this.container.querySelector('[data-catalog-namespace="catalog/cart/trigger/num"]');
            if (catalogCartNum) catalogCartNum.textContent = quantity;

            const catalogCartTotal = this.container.querySelector('[data-catalog-namespace="catalog/cart/total"]');
            if (catalogCartTotal && total !== null) {
                catalogCartTotal.textContent = this.numbro(total).format({thousandSeparated: true, mantissa: 2});
            }
        });

        if (document.querySelector('[data-catalog-namespace="catalog/cart/product"]')) {
            this.updateCart();
        }
    }
}

export default Catalog;

// Usage
// const catalog = new Catalog(actions);
